import { Unsubscribe } from '@reduxjs/toolkit';
import { SlFocusEvent, SlInput } from '@shoelace-style/shoelace';
import '@shoelace-style/shoelace/dist/components/badge/badge';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon';
import '@shoelace-style/shoelace/dist/components/input/input';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label';
import '@shoelace-style/shoelace/dist/components/menu/menu';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ProductSearchQueryArgs, ProductsQuery, ProductStatsQuery, ProductSuggestion, SuggestionsQuery } from '../api/models';
import { NavigateEvent, SearchEvent } from '../events';
import { switchLayout } from '../media-query';
import { Layout, selectLayout, selectProductSearchQueryArgs } from '../state/pages';
import { RootState, store } from '../store';
import { styles as sharedStyles } from '../styles/shared-styles';

import { QueryStatus } from '@reduxjs/toolkit/query';
import { getProductImageUrl, getProductUrl } from '../api/models-transformers';
import { selectProductSearchQuery, selectProductStatsQuery } from '../api/products';
import { selectProductSuggestionsQuery, suggestProducts } from '../api/suggestions';

@customElement('search-query')
export class SearchQuery extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          box-sizing: border-box;
          position: relative;
        }
        .suggestions {
          position: absolute;
          width: 100%;
          background: white;
          border-radius: var(--sl-border-radius-medium);
          box-shadow: var(--sl-shadow-medium);
          z-index: 100;
        }
        sl-menu-item {
          cursor: pointer;
        }
      `
    ]
  }

  @property({ attribute: true, type: Number })
  layout: Layout = Layout.PHONE;

  @property({ attribute: true, state: false, type: String })
  q: string = "";

  @property({ attribute: false, state: true, type: Boolean })
  private showMenu: boolean = false;

  @property({ attribute: false, state: true, type: Object })
  productStatsQuery: ProductStatsQuery | null = null;

  @property({ attribute: false, state: true, type: Object })
  private productSearchQueryArgs: ProductSearchQueryArgs = {};

  @property({ attribute: false, state: true, type: Object })
  private suggestionsSearchQueryArgs: ProductSearchQueryArgs = {};

  @property({ attribute: false, state: true, type: Object })
  private productsQuery: ProductsQuery | null = null;

  @property({ attribute: false, state: true, type: Object })
  private suggestionsQuery: SuggestionsQuery | null = null

  renderCategorySuggestions() {
    if (this.productsQuery?.status === QueryStatus.fulfilled &&
      (this.productSearchQueryArgs.category_ids || [])?.length > 0 &&
      this.productsQuery?.data?.results.length) {
      return html`
        <div class="suggestions">
        <sl-menu>
              <sl-menu-label>Categories</sl-menu-label>
        ${Object.values(this.productsQuery.data.stats?.categoryStats || {})
          // .filter(stat => (this.productSearchQueryArgs?.category_ids || []).indexOf(stat.category)<0)
          .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
          .slice(0, 8)
          .map(categoryStat => html`
                  <sl-menu-item @click=${() => this.selectCategory(categoryStat.category || "")}>
                    ${categoryStat.category} (${this.productStatsQuery?.data?.categoryMap ? this.productStatsQuery?.data?.categoryMap[categoryStat.category].product_count : categoryStat.product_count})
                  </sl-menu-item>
                `)}
              <sl-divider></sl-divider>        
              </sl-menu>
              </div>`
    } else if (this.productStatsQuery?.data?.categoryMap) {
      // show based on the global stats
      return html`
        <div class="suggestions">
        <sl-menu>
              <sl-menu-label>Categories</sl-menu-label>
        ${Object.values(this.productStatsQuery?.data?.categoryMap || {})
          .filter(categoryStat => categoryStat.level === 1)
          .sort((a, b) => a.category.localeCompare(b.category))
          .map(categoryStat => html`
                  <sl-menu-item @click=${() => this.selectCategory(categoryStat.category || "")}>                    
                    ${categoryStat.category} (${categoryStat.product_count})
                  </sl-menu-item>
                `)}
              <sl-divider></sl-divider>        
              </sl-menu>
              </div>`

    } else return nothing;
  }

  renderSearchSuggestions() {
    if (this.suggestionsQuery?.status === QueryStatus.fulfilled &&
      this.suggestionsQuery?.data?.results.length &&
      this.q.length >= 3) return html`
      <div class="suggestions">
      <sl-menu>
            <sl-menu-item @click=${() => this.searchQ()}>
              <sl-icon slot="prefix" name="search"></sl-icon>
                Zoeken naar <strong>${this.q}</strong>
            </sl-menu-item>
            <sl-menu-label>Categories</sl-menu-label>
      ${Object.values(this.suggestionsQuery.data.stats?.categoryStats || {})
          // .filter(stat => (this.productSearchQueryArgs?.category_ids || []).indexOf(stat.category)<0)
          .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
          .slice(0, 5)
          .map(categoryStat => html`
                <sl-menu-item @click=${() => this.selectCategory(categoryStat.category || "")}>
                  ${categoryStat.category} (${categoryStat.product_count})
                </sl-menu-item>
              `)}
            <sl-divider></sl-divider>
            <sl-menu-label>Merken</sl-menu-label>
      ${Object.values(this.suggestionsQuery.data.stats?.brandStats || {})        // .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
          .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
          .slice(0, 5)
          .map(brandStat => html`
                <sl-menu-item @click=${() => this.selectBrand(brandStat.brand || "")}>
                  ${brandStat.brand} (${brandStat.product_count})
                </sl-menu-item>
              `)}
            <sl-divider></sl-divider>
            <sl-menu-label>Suggesties</sl-menu-label>
            ${this.suggestionsQuery.data.results
          .slice(0, 10)
          .map(suggestion => html`
              <sl-menu-item @click=${() => this.gotoProduct(suggestion)}>
                <img class="product-image" @error=${this.setPlaceholderImage} loading="lazy" src="${getProductImageUrl(suggestion.shop, suggestion.product_ref, 200)}" width="45px" height="45px"/>
                ${suggestion.product_name}
              </sl-menu-item>`)}
      </sl-menu>
      </div>`
    else return nothing;
  }

  // in case of error loading the product image
  setPlaceholderImage(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.removeEventListener('error', this.setPlaceholderImage);
    imgElement.src = '/img/no-image.jpg'; // Replace with your fallback image URL
  }

  composeSuggestions() {
    if (this.suggestionsQuery?.status === QueryStatus.fulfilled
      && this.suggestionsQuery?.data?.results.length
      && this.q.length >= 3
      && this.showMenu) {
        return this.renderSearchSuggestions();
      }  else if (this.showMenu) {
        return this.renderCategorySuggestions()
      } else {
        return nothing;
      }
  }

  render() {
    return html`
      <div class="search-area">
      <sl-input  
        placeholder="Zoeken..." 
        clearable
        size=${switchLayout(this.layout, "small", "small", "medium", "large")} 
        autocomplete="off"   
        @focus=${this.onFocus}
        @blur=${this.onBlur}
        @sl-input=${this.onInput}
        enterkeyhint
        pill>
        <sl-icon name="search" slot="prefix"></sl-icon>
      </sl-input>
      ${this.composeSuggestions()}
    `;
  }

  removeCategory(_event: TouchEvent, category: string) {
    const category_ids = this.productSearchQueryArgs.category_ids?.filter(existing => existing !== category);
    const productSearchQueryArgs: ProductSearchQueryArgs = Object.assign({}, this.productSearchQueryArgs, { q: this.q }, { category_ids });
    this.dispatchEvent(new SearchEvent(productSearchQueryArgs));
  }

  private stateUnsubscribe: Unsubscribe = () => { };

  stateChanged(state: RootState) {
    this.layout = selectLayout(state);
    this.productSearchQueryArgs = selectProductSearchQueryArgs(store.getState());
    this.productsQuery = selectProductSearchQuery(store.getState(), this.productSearchQueryArgs);
    this.suggestionsQuery = selectProductSuggestionsQuery(store.getState(), this.suggestionsSearchQueryArgs);
    this.productStatsQuery = selectProductStatsQuery(state);
  }

  connectedCallback() {
    super.connectedCallback();
    this.stateUnsubscribe = store.subscribe(() => {
      const state = store.getState();
      this.stateChanged(state);
    });
    // update immediately
    const state = store.getState();
    this.stateChanged(state);
    // intercept clicks and ESC button
    // Add document click listener
    document.addEventListener('click', (event: MouseEvent) => {
      const menus = this.shadowRoot?.querySelectorAll('.suggestions');
      const input = this.shadowRoot?.querySelector('sl-input');
      const path = event.composedPath();

      const clickedOnMenu = Array.from(menus || []).some(menu => path.includes(menu as EventTarget));
      if (!clickedOnMenu && !path.includes(input as EventTarget)) {
        this.showMenu = false;
      }
    });

    // Add ESC key listener
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.showMenu = false;
      }
    });
  }

  disconnectedCallback() {
    this.stateUnsubscribe();
  }

  async onFocus(_event: SlFocusEvent) {
    this.showMenu = true;
    this.suggest();
  }

  async onBlur(event: Event) {
    const relatedTarget = (event as FocusEvent).relatedTarget as HTMLElement;
    const menus = this.shadowRoot?.querySelectorAll('.suggestions');
    const input = this.shadowRoot?.querySelector('sl-input');

    const focusedOnMenu = Array.from(menus || []).some(menu => menu.contains(relatedTarget));
    if (!focusedOnMenu && !input?.contains(relatedTarget)) {
      this.showMenu = false;
    }
  }

  async onInput(_event: Event) {
    this.showMenu = true;
    this.suggest();
  }

  async suggest() {
    const input = this.shadowRoot!.querySelector('sl-input') as SlInput;
    this.q = input.value; // Add this line to update the q property
    if (this.q.length >= 3) {
      this.suggestionsSearchQueryArgs = Object.assign({}, this.productSearchQueryArgs, { q: this.q });
      store.dispatch(suggestProducts(this.suggestionsSearchQueryArgs));
    }
  }

  resetSuggestions() {
    this.showMenu = false;
  }

  searchQ() {
    const productSearchQueryArgs: ProductSearchQueryArgs = Object.assign({}, this.productSearchQueryArgs, { q: this.q });
    this.dispatchEvent(new SearchEvent(productSearchQueryArgs));
    this.resetSuggestions();
  }

  gotoProduct(suggestion: ProductSuggestion) {
    const productUrl = getProductUrl(suggestion.shop, suggestion.product_ref, suggestion.product_name);
    this.dispatchEvent(new NavigateEvent({ url: productUrl }));
    this.resetSuggestions();
  }

  selectBrand(brand: string) {
    const suggestionsSearchQueryArgs: ProductSearchQueryArgs = Object.assign({}, { q: brand }, this.productSearchQueryArgs); // TODO:implement search brand strict
    this.dispatchEvent(new SearchEvent(suggestionsSearchQueryArgs));
    this.resetSuggestions();
  }

  selectCategory(category: string) {
    const cats = [...(this.productSearchQueryArgs?.category_ids || []), category];
    const args = Object.assign({}, this.productSearchQueryArgs, { category_ids: [...new Set(cats)] }, { q: "" });
    this.dispatchEvent(new SearchEvent(args));
    this.resetSuggestions();
  }
}

